import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { combineLatest,EMPTY,firstValueFrom } from 'rxjs';
import { catchError,map,withLatestFrom } from 'rxjs/operators';
import { isNotNullOrUndefined,stringifyIfNotNullOrUndefined,stringifyIfNotUndefined } from '../../helper/helper';
import { AccountsReceivableLedgerTemplateEntity,AccountsReceivableLedgerTemplateEntityFromBackend } from '../entities/accounts-receivable-ledger-template.entity';
import { LedgerImportDocumentTypes } from '../entities/ledger-import.entity';
import { AccountsReceivableLedgerPostType,AccountsReceivableLedgerTemplatePostType,DaveMutationChangeAccountsReceivableLedgerTemplateArgs,DaveMutationCreateAccountsReceivableLedgerTemplateArgs } from '../graphql-types';
import { AccountsReceivableLedgerTemplateResolver } from '../guards/accounts-receivable-ledger-template.resolver';
import { getFetched$ } from '../helper/helper';
import { State } from '../State';
import { AccountsReceivableLedgerTemplateActionTypes } from '../State/actions/accounting.actions';
import { BaseActionTypes } from '../State/actions/base.actions';
import { getAccountsReceivableLedgerTemplateDictionary,getAccountsReceivableLedgerTemplates,getAccountsReceivableLedgerTemplatesFetched } from '../State/selectors/accounting.selector';
import { ArlDataService } from './arl-data.service';
import { HttpService } from './http.service';
import { AccountsReceivableLedgerEntity } from '../entities/accounts-receivable-ledger.entity';

export const arlTemplatePostQuery = (Payload: AccountsReceivableLedgerTemplatePostType, currencyCalcFactor = 100) => {
    return `{
      ${stringifyIfNotNullOrUndefined(Payload, 'tax')}
      ${stringifyIfNotNullOrUndefined(Payload, 'currencyCode')}
      ${stringifyIfNotNullOrUndefined(Payload, 'bookingText')}
      ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
      ${stringifyIfNotNullOrUndefined(Payload, 'commissionTypeId')}
      ${stringifyIfNotNullOrUndefined(Payload, 'eventTypeId')}
      ${stringifyIfNotNullOrUndefined(Payload, 'quantity')}
      ${stringifyIfNotNullOrUndefined(Payload, 'information')}
      ${stringifyIfNotNullOrUndefined(Payload, 'quantityTypeId')}
      ${isNotNullOrUndefined(Payload.costAmount) ? 'costAmount:' + (Payload.costAmount * currencyCalcFactor).toFixed(0) : ''}
      ${isNotNullOrUndefined(Payload.baseCost) ? 'baseCost:' + (Payload.baseCost * currencyCalcFactor).toFixed(0) : ''}
      multiplikator: ${JSON.stringify(Payload.multiplikator)}
      ${stringifyIfNotNullOrUndefined(Payload, 'customProperties')}
      ${stringifyIfNotNullOrUndefined(Payload, 'aRLTemplateTypeId')}
      ${stringifyIfNotNullOrUndefined(Payload, 'longInformation')}
      ${stringifyIfNotNullOrUndefined(Payload, 'showLongInformation')}
      ${stringifyIfNotNullOrUndefined(Payload, 'parentId')}
      ${stringifyIfNotNullOrUndefined(Payload, 'arlTemplateIds')}
      ${stringifyIfNotNullOrUndefined(Payload, 'inheritFromChildren')}
      ${stringifyIfNotNullOrUndefined(Payload, 'isVisible')}
      ${stringifyIfNotUndefined(Payload, 'resourceId')}
      ${stringifyIfNotUndefined(Payload, 'jobSpecificationId')}
      ${stringifyIfNotUndefined(Payload, 'materialId')}
      ${stringifyIfNotUndefined(Payload, 'quantityFormula')}
      ${isNotNullOrUndefined(Payload.baseCostGross) ? 'baseCostGross:' + (Payload.baseCostGross * currencyCalcFactor).toFixed(0) : ''}
      ${Payload.type ? `type: ${Payload.type}` : ''}
    }`;
};
enum ErrorCodes {
    Add = 'Buchungspositionsvorlage hinzufügen fehlgeschlagen',
    Modify = 'Buchungspositionsvorlage ändern fehlgeschlagen',
}
@Injectable({
    providedIn: 'root',
})
export class ArlTemplateDataService {
    constructor(
        private store$: Store<State>,
        private gatewayHttpService: HttpService,
        private arlTemplateResolver: AccountsReceivableLedgerTemplateResolver,
        private arlDataService: ArlDataService,
    ) {}
    public addArlTemplates(payload: DaveMutationCreateAccountsReceivableLedgerTemplateArgs[], currencyCalcFactor = 100): Promise<AccountsReceivableLedgerTemplateEntity[]> {
        const queryString = `mutation {
          createAccountsReceivableLedgerTemplateMultiple(posts: [
        ${payload.map((Payload) => arlTemplatePostQuery(Payload, currencyCalcFactor)).join(', ')}
        ]) {${AccountsReceivableLedgerTemplateEntity.GqlFields.join(',')}}}`;
        return firstValueFrom(this.gatewayHttpService
            .graphQl({ query: queryString }).pipe(
                withLatestFrom(this.store$.select(getAccountsReceivableLedgerTemplates)),
                map(([res, arlTemplates]) => {
                    if (res?.createAccountsReceivableLedgerTemplateMultiple?.every((r) => !!r)) {
                        const entitys = res.createAccountsReceivableLedgerTemplateMultiple.map((r) => AccountsReceivableLedgerTemplateEntityFromBackend(r));
                        this.store$.dispatch(
                            AccountsReceivableLedgerTemplateActionTypes.UpdateAccountsReceivableLedgerTemplate({
                                Payload: [...arlTemplates, ...entitys],
                            }),
                        );
                        return entitys;
                    } else {
                        throw 'wrong response';
                    }
                }),
                catchError((err, caught) => {
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return EMPTY;
                }),
            ),
        );
    }
    public changeArlTemplatess(payload: DaveMutationChangeAccountsReceivableLedgerTemplateArgs[], currencyCalcFactor = 100): Promise<AccountsReceivableLedgerTemplateEntity[]> {
        // const Payload = payload[0];
        console.log(payload);
        return firstValueFrom(
            combineLatest(
                payload.map((Payload) => {
                    const queryString = `mutation {
          changeAccountsReceivableLedgerTemplate(
    id: ${Payload.id}
    ${stringifyIfNotNullOrUndefined(Payload, 'tax')}
    ${stringifyIfNotNullOrUndefined(Payload, 'currencyCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'information')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bookingText')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionTypeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'eventTypeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'quantity')}
    ${stringifyIfNotUndefined(Payload, 'quantityFormula')}
    quantityTypeId: ${JSON.stringify(Payload.quantityTypeId)}
    ${isNotNullOrUndefined(Payload.costAmount) ? 'costAmount:' + (Payload.costAmount * currencyCalcFactor).toFixed(0) : ''}
    ${isNotNullOrUndefined(Payload.baseCost) ? 'baseCost:' + (Payload.baseCost * currencyCalcFactor).toFixed(0) : ''}
    multiplikator: ${JSON.stringify(Payload.multiplikator)}
    ${stringifyIfNotNullOrUndefined(Payload, 'customProperties')}
    aRLTemplateTypeId: ${JSON.stringify(Payload.aRLTemplateTypeId)}
              ${stringifyIfNotNullOrUndefined(Payload, 'longInformation')}
              ${stringifyIfNotNullOrUndefined(Payload, 'showLongInformation')}
              ${stringifyIfNotNullOrUndefined(Payload, 'parentId')}
              ${stringifyIfNotNullOrUndefined(Payload, 'arlTemplateIds')}
              ${stringifyIfNotNullOrUndefined(Payload, 'inheritFromChildren')}
              ${stringifyIfNotNullOrUndefined(Payload, 'isVisible')}
              ${stringifyIfNotUndefined(Payload, 'resourceId')}
              ${stringifyIfNotUndefined(Payload, 'jobSpecificationId')}
              ${stringifyIfNotUndefined(Payload, 'materialId')}
              ${isNotNullOrUndefined(Payload.baseCostGross) ? 'baseCostGross:' + (Payload.baseCostGross * currencyCalcFactor).toFixed(0) : ''}

    ${Payload.type ? `type: ${Payload.type}` : ''}

          ) {${AccountsReceivableLedgerTemplateEntity.GqlFields.join(',')}}
        }
        `;
                    return this.gatewayHttpService
                        .graphQl({ query: queryString })
                        .pipe
                        // map(res => res?.createAccountsReceivableLedger ? [res.createAccountsReceivableLedger] : null),
                        ();
                }),
            ).pipe(
                withLatestFrom(this.store$.select(getAccountsReceivableLedgerTemplates)),
                map(([res, bookingPositions]) => {
                    //     if (res && res.changeAccountsReceivableLedgerTemplate) {
                    //         return AccountsReceivableLedgerTemplateActionTypes.UpdateAccountsReceivableLedgerTemplate({
                    //             Payload: [...bookingPositions.filter((bp) => bp.Id !== res.changeAccountsReceivableLedgerTemplate.id), AccountsReceivableLedgerTemplateEntityFromBackend(res.changeAccountsReceivableLedgerTemplate)],
                    //         });
                    //     } else {
                    //         return BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Modify } });
                    //     }
                    // }),
                    // map(([res]) => {
                    if (res?.every((r) => r?.changeAccountsReceivableLedgerTemplate)) {
                        const entitys = res.map((r) => AccountsReceivableLedgerTemplateEntityFromBackend(r.changeAccountsReceivableLedgerTemplate));
                        this.store$.dispatch(
                            AccountsReceivableLedgerTemplateActionTypes.UpdateAccountsReceivableLedgerTemplate({
                                Payload: [...bookingPositions.filter((bp) => entitys.every((e) => e.Id !== bp.Id)), ...entitys],
                            }),
                        );
                        return entitys;
                    } else {
                        throw 'wrong response';
                    }
                }),
                catchError((err, caught) => {
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return EMPTY;
                }),
            ),
        );
    }
    getParentsQuantity(id: number, oldQuantiy: number, sourceDictionary: Dictionary<AccountsReceivableLedgerTemplateEntity>): number {
        let parent = sourceDictionary[id];
        let quantity = oldQuantiy + parent.Quantity;
        if (parent) {
            if (parent.ParentId) {
                this.getParentsQuantity(parent.ParentId, quantity, sourceDictionary);
            }
        }
        if (quantity === 0) {
            quantity = 1;
        }
        return quantity;
    }
    public async addArlTemplatesforOrder(sourceArls: number[], ledgerImportId: number, customerId: number | null | undefined, commissionId: number | null | undefined, documentType: LedgerImportDocumentTypes) {
        const sourceDictionary = await firstValueFrom(getFetched$(this.store$, getAccountsReceivableLedgerTemplatesFetched, getAccountsReceivableLedgerTemplateDictionary, this.arlTemplateResolver));
        const getPayload = (source: AccountsReceivableLedgerTemplateEntity): AccountsReceivableLedgerPostType => {
            let parentQuantity = 1;
            parentQuantity = this.getParentsQuantity(source.ParentId, 0, sourceDictionary);
            return {
                itemNumber: AccountsReceivableLedgerEntity.autogenerateItemNumber,
                quantity: source.Quantity * parentQuantity || 0,
                tax: source.Tax,
                currencyCode: source.CurrencyCode,
                bookingText: source.BookingText,
                baseCost: source?.CostAmount ? source.CostAmount : source.BaseCost || 0,
                costAmount: source.CostAmount,
                ledgerImportId: ledgerImportId,
                documentType: documentType,
                customerId: customerId !== undefined ? customerId : null,
                commissionId: commissionId !== undefined ? commissionId : null,
                information: source.Information,
                quantityTypeId: source.QuantityTypeId,
                multiplikator: source.Multiplier,
                customProperties: JSON.stringify({
                    ...source.CustomProperties,
                }),
                isVisible: source.IsVisible,
                longInformation: source.Longtext,
                showLongInformation: source.ShowLongtext,
                type: source.Type as any,
                resourceId: source.ResourceId,
                materialId: source.MaterialId,
                jobSpecificationId: source.JobSpecificationId,
                hideInGroupSummary: false,
                quantityFormula: source.QuantityFormula,
            };
        };
        const arlsToAdd: AccountsReceivableLedgerPostType[] = [];
        function addArls(source: AccountsReceivableLedgerTemplateEntity) {
            arlsToAdd.push(getPayload(source));
        }

        sourceArls.forEach((sourceArlId) => {
            const src = sourceDictionary[sourceArlId];
            src.ArlTemplateIds.forEach((arlTemplateId) => {
                const calculatedTemplate = sourceDictionary[arlTemplateId];
                if (calculatedTemplate.Type === 'MaterialArl' || calculatedTemplate.Type === 'Other') {
                    addArls(calculatedTemplate);
                }
            });
        });

        let ids: number[] = [];
        let mergedData: AccountsReceivableLedgerPostType[] = [];

        arlsToAdd.forEach((d) => {
            if (d?.materialId != null) {
                const materialId = d.materialId;

                const existing = mergedData.find((item) => item.materialId === materialId);
                if (existing) {
                    existing.quantity += d.quantity ?? 0;
                } else {
                    ids.push(materialId);
                    mergedData.push({ ...d });
                }
            } else {
                mergedData.push({ ...d });
            }
        });
        const responseArls = await this.arlDataService.addArls(mergedData);

        return responseArls;
    }
}
